import { CircularProgress, Popover, Tooltip } from '@mui/material';
import moment from 'moment-timezone';
import { useRef, useState } from 'react';
import { twMerge } from 'tailwind-merge';

import PlaybackIcon from '../../../assets/svg/PlaybackIcon';
import RefreshIcon from '../../../assets/svg/RefreshIcon';
import TranscriptIcon from '../../../assets/svg/TranscriptIcon';
import {
  CALL_LOG_DETAILS_ITEM,
  DATE_TIME_FORMAT,
  RECORDING_RELATED_DETAILS_ITEMS,
} from '../../../constants';
import { applyServerTimezone } from '../../../helpers';
import { useAuthContext } from '../../../hooks';
import { ListenRecordingModal, ReadCallNotesModal } from '../../Modals';
import { Button, IconButton, TableBodyCell } from '../../Shared';

const STATE = {
  HAS_RECORDING_DATA: 'hasRecordingData',
  IS_RECORDING_DATA_LOADING: 'isRecordingDataLoading',
  IS_RECORDING_DATA_ERROR: 'isRecordingDataError',
};

const getState = (isRecordingDataLoading, isRecordingDataError) => {
  if (isRecordingDataLoading) return STATE.IS_RECORDING_DATA_LOADING;
  if (isRecordingDataError) return STATE.IS_RECORDING_DATA_ERROR;
  return STATE.HAS_RECORDING_DATA;
};

const getFormattedRecordDate = (date, format) =>
  moment(applyServerTimezone(date)).format(format);

export const TableCellDetails = ({ callRecordingID, withBorderBottom }) => {
  const { recordingVisible } = useAuthContext();
  const abortControllerRef = useRef(null);

  const [anchorEl, setAnchorEl] = useState(null);
  const [recordingData, setRecordingData] = useState(null);
  const [isRecordingDataLoading, setIsRecordingDataLoading] = useState(true);
  const [isRecordingDataError, setIsRecordingDataError] = useState(false);
  const [isPlaybackModalOpened, setIsPlaybackModalOpened] = useState(false);
  const [isTranscriptModalOpened, setIsTranscriptModalOpened] = useState(false);

  const openPlaybackModal = () => setIsPlaybackModalOpened(true);
  const closePlaybackModal = () => setIsPlaybackModalOpened(false);
  const openTranscriptModal = () => setIsTranscriptModalOpened(true);
  const closeTranscriptModal = () => setIsTranscriptModalOpened(false);

  const fetchCallRecording = async () => {
    if (recordingData) return;

    setIsRecordingDataLoading(true);
    setIsRecordingDataError(false);

    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }

    abortControllerRef.current = new AbortController();

    try {
      const response = await fetch('/admin/call-logs/details', {
        signal: abortControllerRef.current.signal,
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ callRecordingID }),
      });

      if (response.status !== 200) throw new Error();

      const data = await response.json();
      const callRecording = data.callRecordings.query1[0];
      setRecordingData(callRecording);
      setIsRecordingDataLoading(false);
    } catch (e) {
      if (e?.name === 'AbortError') return;
      setIsRecordingDataError(true);
      setIsRecordingDataLoading(false);
    }
  };

  const onOpenPopover = e => {
    setAnchorEl(e.currentTarget);
    fetchCallRecording();
  };

  const onClosePopover = () => {
    setAnchorEl(null);
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
  };

  const list = [
    {
      title: CALL_LOG_DETAILS_ITEM.PLAYBACK,
      icon: <PlaybackIcon className="w-4 h-4 text-brand-900" />,
      disabled: !recordingData || !recordingData?.RecordingURL,
      onClick: () => {
        openPlaybackModal();
        onClosePopover();
      },
    },
    {
      title: CALL_LOG_DETAILS_ITEM.TRANSCRIPT,
      icon: <TranscriptIcon className="w-4 h-4 text-brand-900" />,
      disabled:
        !recordingData ||
        !recordingData?.CLTranscript ||
        !recordingData?.Transcript,
      onClick: () => {
        openTranscriptModal();
        onClosePopover();
      },
    },
  ].filter(
    ({ title }) =>
      recordingVisible || !RECORDING_RELATED_DETAILS_ITEMS.includes(title),
  );
  const state = getState(isRecordingDataLoading, isRecordingDataError);
  const open = Boolean(anchorEl);

  return (
    <>
      <TableBodyCell
        align="center"
        withBorderBottom={withBorderBottom}
        width="56px"
      >
        {list.length > 0 && (
          <>
            <Tooltip title="Details" placement="top">
              <span>
                <IconButton
                  buttonProps={{
                    className: 'w-8 h-8',
                    onClick: onOpenPopover,
                  }}
                  iconProps={{
                    name: 'dots',
                    className: 'h-5',
                  }}
                />
              </span>
            </Tooltip>
            <Popover
              open={open}
              slotProps={{
                paper: {
                  className:
                    '!rounded-xl mt-1 !shadow-[0_4px_20px_0_rgba(17,17,58,0.08)] bg-white w-[200px]',
                },
              }}
              anchorEl={anchorEl}
              onClose={onClosePopover}
              anchorOrigin={{
                vertical: 'bottom',
                horizontal: 'left',
              }}
            >
              {state === STATE.IS_RECORDING_DATA_LOADING && (
                <div className="h-[70px] flex justify-center items-center">
                  <CircularProgress size={40} sx={{ color: '#1F68A2' }} />
                </div>
              )}
              {state === STATE.IS_RECORDING_DATA_ERROR && (
                <div className="flex justify-center items-center flex-col p-2 gap-2">
                  <span className="text-base-1000 text-[16px]/[20px] font-semibold">
                    Failed to load data
                  </span>
                  <Button
                    title="Reload"
                    variant="text"
                    className="h-auto text-[14px]/[18px] py-1 px-3.5 text-destructive-500"
                    iconStart={
                      <RefreshIcon className="w-4 h-4 text-destructive-500" />
                    }
                    onClick={fetchCallRecording}
                  />
                </div>
              )}
              {state === STATE.HAS_RECORDING_DATA && (
                <div className="p-0.5 flex flex-col gap-0.5">
                  {list.map(({ title, icon, disabled, onClick }, idx) => (
                    <div
                      key={idx}
                      className={twMerge(
                        'flex items-center gap-2 py-1.5 px-2 cursor-pointer',
                        disabled && 'pointer-events-none opacity-50',
                      )}
                      onClick={onClick}
                    >
                      {icon}
                      <span className="font-medium text-[14px]/[20px] text-base-1000">
                        {title}
                      </span>
                    </div>
                  ))}
                </div>
              )}
            </Popover>
          </>
        )}
      </TableBodyCell>
      {isPlaybackModalOpened && (
        <ListenRecordingModal
          open={isPlaybackModalOpened}
          onClose={closePlaybackModal}
          recording={{
            contactName: recordingData.ContactName,
            date: getFormattedRecordDate(
              recordingData.DateAndTimeOfCall,
              DATE_TIME_FORMAT.MM_DD_YYYY_hh_mm_A,
            ),
            recordingURL: recordingData.RecordingURL,
          }}
        />
      )}
      {isTranscriptModalOpened && (
        <ReadCallNotesModal
          open={isTranscriptModalOpened}
          onClose={closeTranscriptModal}
          recording={{
            tenant: recordingData.Tenant,
            callerName: recordingData.CallerName,
            contactName: recordingData.ContactName,
            companyName: recordingData.ContactCompany,
            date: getFormattedRecordDate(
              recordingData.DateAndTimeOfCall,
              DATE_TIME_FORMAT.MM_DD_YYYY_hh_mm_A,
            ),
            callDuration: recordingData.CallDuration,
            callOutcome: recordingData.CallOutcome,
            clTranscript: recordingData.CLTranscript,
          }}
        />
      )}
    </>
  );
};
